'use client';

import { CerAdmins } from '@/components/admin/cer-admins';
import { CerBrandingPanel } from '@/components/admin/cer-branding-panel';
import CerFeaturesPage from './cer-features';
import { api, apiFetch } from '@/lib/utils/api';
import { StatusBlock } from '@slims/ui/molecules';
import { CerDetailsView, type CerDetailsForm } from '@slims/ui/organisms';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { useParams, useRouter } from '@/lib/next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';

/**
 * CER "Dettagli" form: name and comune of the community. Energy and
 * cabina-primaria data live on the Configuration Dettagli form instead.
 */
export default function CerDetailsPage() {
	const { id } = useParams<{ id: string }>();
	const role = useAuthStore((s) => s.role);
	const loaded = useAuthStore((s) => s.loaded);
	const availableOrgs = useAuthStore((s) => s.availableOrgs);
	const isSuperadmin = role === 'superadmin';
	if (!loaded) return <StatusBlock kind="loading" size="lg" message="Caricamento..." />;
	if (!id || (!isSuperadmin && !availableOrgs.some((org) => org.id === id && org.role === 'admin')))
		return <Navigate to="/dashboard" replace />;
	return <CerDetailsFormPage key={id} id={id} isSuperadmin={isSuperadmin} />;
}

function CerDetailsFormPage({ id, isSuperadmin }: { id: string; isSuperadmin: boolean }) {
	const router = useRouter();
	const fetchAuth = useAuthStore((s) => s.fetchAuth);
	const { refetch: refetchScopeTree } = useScopeTree();
	const [nome, setNome] = useState('');
	const [form, setForm] = useState<CerDetailsForm>({});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [loadError, setLoadError] = useState(false);
	const [error, setError] = useState('');

	// Modifiche non ancora salvate: un aggiornamento in tempo reale non deve
	// sovrascriverle, e in quel caso si limita ad avvisare.
	const dirtyRef = useRef(false);
	const [remoteChange, setRemoteChange] = useState(false);

	const s = (k: string) => (form[k] as string) ?? '';
	const set = (k: string, v: string | boolean) => {
		dirtyRef.current = true;
		// L'errore dell'ultimo salvataggio non vale più appena si corregge un
		// campo: restava in rosso anche a dato già giusto (N-01).
		setError('');
		setForm((f) => ({ ...f, [k]: v }));
	};

	/** Legge la CER. In background (`sfondo`) un errore non sostituisce la pagina. */
	const load = useCallback(
		(sfondo: boolean) => {
			if (!id) return;
			api
				.get<Record<string, unknown>>(`/api/admin/orgs/${id}`)
			.then((d) => {
				const str = (v: unknown) => (v == null ? '' : String(v));
				setNome(str(d.nome));
				setForm({
					nome: str(d.nome),
					comune: str(d.comune),
					disabled: !!d.disabled,
				});
				try {
					const settings = JSON.parse((d.settings as string) ?? '{}') as Record<string, unknown>;
					// I pesi del vecchio criterio di riparto non si riscrivono più.
					delete settings.ripartoCustomPropKwh;
					delete settings.ripartoCustomFixed;
					// Preserve unrelated settings keys on save.
					set('__settings', JSON.stringify(settings));
				} catch {
					set('__settings', '{}');
				}
				dirtyRef.current = false;
				setRemoteChange(false);
				setLoading(false);
			})
			.catch(() => {
				if (sfondo) return;
				setLoadError(true);
				setLoading(false);
			});
		},
		[id],
	);

	useEffect(() => {
		load(false);
	}, [load]);

	// Realtime: la CER può cambiare da un altro pannello o da un'altra scheda. Senza modifiche locali la pagina si riallinea da sola;
	// con modifiche locali resta com'è e mostra un avviso.
	useEffect(() => {
		if (!id) return;
		const onRemote = () => {
			if (dirtyRef.current) setRemoteChange(true);
			else load(true);
		};
		const unsubscribes = [
			subscribeToTable(
				`cer-details-org-${id}`,
				{ event: '*', schema: 'public', table: 'organizations', filter: `id=eq.${id}` },
				onRemote,
			),
		];
		return () => {
			for (const unsubscribe of unsubscribes) unsubscribe();
		};
	}, [id, load]);

	async function handleSave() {
		setError('');
		if (!s('nome').trim()) {
			setError('La denominazione è obbligatoria');
			return;
		}
		setSaving(true);
		const NON_COLUMN = new Set(['__settings']);
		const body: Record<string, unknown> = {};
		for (const [k, v] of Object.entries(form)) {
			if (NON_COLUMN.has(k) || (k === 'disabled' && !isSuperadmin)) continue;
			body[k] = v;
		}
		let settingsObj: Record<string, unknown> = {};
		try {
			settingsObj = JSON.parse(s('__settings') || '{}') as Record<string, unknown>;
		} catch {
			settingsObj = {};
		}
		body.settings = JSON.stringify(settingsObj);

		// A salvataggio riuscito si torna all'elenco, come nel dettaglio di una
		// configurazione.
		try {
			const res = await apiFetch(`/api/admin/orgs/${id}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			});
			if (!res.ok) {
				const result = (await res.json().catch(() => ({}))) as { error?: string };
				setError(result.error ?? 'Errore nel salvataggio');
				return;
			}
			toast.success('CER salvata');
			dirtyRef.current = false;
			setRemoteChange(false);
			await Promise.all([refetchScopeTree(), fetchAuth()]);
			router.push('/cer');
		} catch {
			setError('Errore di rete. Riprova a salvare.');
		} finally {
			setSaving(false);
		}
	}

	return (
		<CerDetailsView
			branding={<CerBrandingPanel key={id} orgId={id} cerName={nome} />}
			features={isSuperadmin ? <CerFeaturesPage /> : undefined}
			admins={isSuperadmin ? <CerAdmins cerId={id} /> : undefined}
			nome={nome}
			form={form}
			loading={loading}
			loadError={loadError}
			saving={saving}
			error={error}
			onDismissError={() => setError('')}
			canDisable={isSuperadmin}
			onFieldChange={set}
			onSave={() => void handleSave()}
			onCancel={() => router.push('/cer')}
			remoteChange={remoteChange}
			onReloadRemote={() => load(true)}
		/>
	);
}
